/**
 * JulietteThreadTabs — Barre d'onglets des conversations (un fil par client).
 *
 * Facon Chrome dans une colonne de 380px : l'onglet ACTIF affiche le nom complet
 * (+ croix pour fermer), les autres se reduisent en pastilles (initiales), et le
 * fil "General" est epingle en tete avec une icone maison. Max 5 onglets, dedup
 * + tri LRU delegues a listOpenTabs.
 *
 * La bascule automatique de Juliette (routage serveur par contact) devient ainsi
 * VISIBLE : l'onglet actif change sous les yeux de l'agent, et "revenir" =
 * recliquer la pastille du fil precedent. Pas de bandeau d'annonce necessaire.
 *
 * Le "+" (facon Chrome) ouvre une page blanche en 1 clic : nouveau fil General
 * vierge (garde anti-doublons via getOrCreateFreshChatThread cote parent).
 * C'est aussi la "purge douce" : l'ancien General sort de la barre (dedup LRU)
 * et reste dans l'Historique. La barre est donc TOUJOURS visible en vue
 * Echange, forme minimale "maison + plus".
 */

import type { MouseEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Home, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { listOpenTabs, closeTab, type JulietteThreadTab } from "@/lib/juliette-session";

interface JulietteThreadTabsProps {
  teamId: string;
  activeSessionId: string | null;
  /** Bascule vers ce fil (reutilise handleSelectThread du panneau). */
  onSelect: (sessionId: string) => void;
  /** "+" : nouvelle conversation Generale vierge (reutilise handleNewChat du panneau). */
  onNewChat: () => void;
}

/** Initiales d'un nom pour la pastille ("Gad Elmaleh" -> "GE", "Brigitte" -> "BR"). */
function initials(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function JulietteThreadTabs({ teamId, activeSessionId, onSelect, onNewChat }: JulietteThreadTabsProps) {
  const queryClient = useQueryClient();

  const { data: tabs = [] } = useQuery({
    queryKey: ["juliette-tabs", teamId],
    enabled: !!teamId,
    queryFn: () => listOpenTabs(teamId),
    // Le routage serveur cree des fils sans passer par l'UI : on rafraichit
    // regulierement en plus des invalidations explicites (onSessionRouted).
    refetchInterval: 30_000,
  });

  const general = tabs.find((t) => t.contactId === null) ?? null;
  const contactTabs = tabs.filter((t) => t.contactId !== null);

  const handleClose = async (tab: JulietteThreadTab, e: MouseEvent) => {
    e.stopPropagation();
    try {
      await closeTab(tab.sessionId);
    } finally {
      void queryClient.invalidateQueries({ queryKey: ["juliette-tabs", teamId] });
    }
    // Fermer l'onglet actif ramene sur General, sinon le 1er fil restant.
    if (tab.sessionId === activeSessionId) {
      const fallback =
        general?.sessionId ?? contactTabs.find((t) => t.sessionId !== tab.sessionId)?.sessionId;
      if (fallback) onSelect(fallback);
    }
  };

  return (
    <div
      className="relative z-10 flex shrink-0 items-center gap-1.5 overflow-x-auto px-3 pb-2"
      role="tablist"
      aria-label="Conversations ouvertes"
    >
      {/* General epingle (maison), comme un onglet epingle Chrome. */}
      {general && (
        <button
          type="button"
          role="tab"
          aria-selected={general.sessionId === activeSessionId}
          onClick={() => onSelect(general.sessionId)}
          title="Général"
          aria-label="Conversation générale"
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors",
            general.sessionId === activeSessionId
              ? "border-white/25 bg-white/15 text-white"
              : "border-white/10 bg-white/[0.04] text-white/50 hover:bg-white/10 hover:text-white/80",
          )}
        >
          <Home className="h-4 w-4" />
        </button>
      )}

      {contactTabs.map((tab) => {
        const isActive = tab.sessionId === activeSessionId;
        if (!isActive) {
          // Pastille reduite : initiales seulement, le nom complet en title.
          return (
            <button
              key={tab.sessionId}
              type="button"
              role="tab"
              aria-selected={false}
              onClick={() => onSelect(tab.sessionId)}
              title={tab.label}
              aria-label={`Conversation ${tab.label}`}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-[11px] font-semibold tracking-wide text-white/60 transition-colors hover:bg-white/10 hover:text-white/90"
            >
              {initials(tab.label)}
            </button>
          );
        }
        // Onglet actif : nom complet + fermeture.
        return (
          <div
            key={tab.sessionId}
            role="tab"
            aria-selected
            className="flex h-8 min-w-0 shrink items-center gap-1 rounded-lg border border-white/25 bg-white/15 pl-2.5 pr-1 text-white"
          >
            <span className="truncate text-xs font-medium">{tab.label}</span>
            <button
              type="button"
              onClick={(e) => void handleClose(tab, e)}
              aria-label={`Fermer la conversation ${tab.label}`}
              title="Fermer (reste dans l'Historique)"
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white/60 transition-colors hover:bg-white/20 hover:text-white"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}

      {/* "+" : page blanche en 1 clic, facon Chrome. */}
      <button
        type="button"
        onClick={onNewChat}
        title="Nouvelle conversation"
        aria-label="Nouvelle conversation"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-white/50 transition-colors hover:bg-white/10 hover:text-white/90"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}
